import React, { useState } from 'react';
import { Wrench, EnvelopeSimple, Lock, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

export function MaintenancePage({ message }) {
  const { login } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user?.role === 'admin') {
        toast.success('Welcome back, Admin');
        setOpen(false);
      } else {
        toast.error('Only admin accounts can access during maintenance');
      }
    } catch {
      toast.error('Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-6" data-testid="maintenance-page">
      <div className="text-center max-w-lg">
        <div className="w-24 h-24 rounded-full bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-8">
          <Wrench className="text-[#F59E0B] text-5xl" />
        </div>
        <h1 className="text-3xl font-semibold text-white mb-4">Under Maintenance</h1>
        <p className="text-white/60 text-lg leading-relaxed mb-6">
          {message || "We're performing scheduled maintenance. We'll be back shortly."}
        </p>
        <div className="flex items-center justify-center gap-2 text-white/30 text-sm mb-8">
          <div className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse"></div>
          <span>Gospeleon will be back soon</span>
        </div>
        <Button
          variant="ghost"
          onClick={() => setOpen(true)}
          className="text-white/30 hover:text-white/60 text-xs underline underline-offset-4"
          data-testid="maintenance-admin-login-btn"
        >
          Admin Login
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141417] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Admin Login</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4 pt-2">
            <div className="relative">
              <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                type="email"
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
                data-testid="maintenance-login-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                type={showPw ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 bg-white/5 border-white/10 text-white"
                data-testid="maintenance-login-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60">
                {showPw ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F59E0B] text-black rounded-full font-bold hover:bg-[#D97706]"
              data-testid="maintenance-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
